<!-- Template du composant Location Picker -->
<div class="location-picker-container">
  <!-- Section de recherche -->
  <div class="search-section">
    <div class="relative mb-3">
      <!-- Input de recherche -->
      <div class="relative">
        <input
          type="text"
          [(ngModel)]="searchQuery"
          (input)="onSearchInput()"
          (focus)="onSearchFocus()"
          (blur)="closeSuggestions()"
          placeholder="Rechercher une adresse..."
          class="w-full px-3 py-2 pl-10 pr-3 text-sm text-gray-900 dark:text-white border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          [class.border-green-500]="validatedAddress"
          [class.border-red-500]="validationError"
          />

          <!-- Icône de recherche -->
          <div class="absolute left-3 top-2.5 text-gray-400">
            @if (!isSearching) {
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                ></path>
              </svg>
            }

            <!-- Spinner pendant la recherche -->
            @if (isSearching) {
              <svg
                class="w-4 h-4 animate-spin text-blue-500"
                fill="none"
                viewBox="0 0 24 24"
                >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            }
          </div>

          <!-- Checkmark si validé -->
          @if (validatedAddress && !isValidating) {
            <div
              class="absolute right-3 top-2.5 text-green-500"
              >
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clip-rule="evenodd"
                ></path>
              </svg>
            </div>
          }
        </div>
      </div>

      <!-- Liste de suggestions (AVANT la carte) -->
      @if (showSuggestions && suggestions.length > 0) {
        <div
          class="mb-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg dark:shadow-slate-900/50 max-h-60 overflow-y-auto"
          >
          @for (suggestion of suggestions; track suggestion; let i = $index) {
            <div
              (mousedown)="selectSuggestion(suggestion)"
              class="px-3 py-2 hover:bg-blue-50 dark:bg-blue-900/20 cursor-pointer border-b border-gray-100 dark:border-slate-700 last:border-b-0 transition-colors"
              >
              <div class="flex items-start">
                <svg
                  class="w-4 h-4 text-gray-400 mt-0.5 mr-2 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  ></path>
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  ></path>
                </svg>
                <div class="flex-1 min-w-0">
                  <p class="text-xs font-medium text-gray-900 dark:text-white">
                    {{ suggestion.label }}
                  </p>
                  <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {{ suggestion.context }}
                  </p>
                </div>
              </div>
            </div>
          }
        </div>
      }

      <!-- Bouton géolocalisation -->
      <button
        (click)="useCurrentLocation()"
        [disabled]="isValidating"
        class="mt-2 w-full flex items-center justify-center px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-100 dark:bg-blue-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
        <svg
          class="w-4 h-4 mr-1.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
          ></path>
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
          ></path>
        </svg>
        📍 Utiliser ma position actuelle
      </button>

      <!-- Message d'erreur -->
      @if (validationError) {
        <div
          class="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start"
          >
          <svg
            class="w-4 h-4 text-red-500 mt-0.5 mr-1.5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
            >
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clip-rule="evenodd"
            ></path>
          </svg>
          <p class="text-xs text-red-800 dark:text-red-300">{{ validationError }}</p>
        </div>
      }
    </div>

    <!-- Carte interactive -->
    @if (showMapContainer || mapInitialized || validatedAddress) {
      <div
        class="map-section mt-3 rounded-lg overflow-hidden border border-gray-300 dark:border-slate-600 shadow-sm dark:shadow-slate-900/30"
        >
        <div #mapContainer [style.height]="mapHeight" class="w-full"></div>
      </div>
    }

    <!-- ⚠️ Hors rayon d'action -->
    @if (isOutsideRadius) {
      <div
        class="mt-2 flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 rounded-lg"
        >
        <span class="text-lg leading-none">⚠️</span>
        <div>
          <p class="text-sm font-medium text-amber-800 dark:text-amber-300">Lieu hors rayon d'action</p>
          <p class="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
            Ce lieu est à {{ distanceFromCenterKm }} km de votre base, au-delà de
            votre rayon d'action de {{ actionRadius }} km.
          </p>
        </div>
      </div>
    }

    <!-- Infos trajet OSRM (distance routière / durée réelle) -->
    @if (distanceFromCenterKm !== null || isLoadingRoute) {
      <div
        class="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-700 dark:text-gray-300 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 rounded-lg px-4 py-2"
        >
        @if (!isLoadingRoute) {
          <span
            >📏 <strong>{{ distanceFromCenterKm }} km</strong> par la route</span
            >
            <span
              >🕐
              <strong
                >{{
                travelTimeMin! >= 60
                ? (travelTimeMin! / 60 | number: "1.0-0") + "h"
                : ""
                }}{{
                travelTimeMin! >= 60
                ? travelTimeMin! % 60 > 0
                ? (travelTimeMin! % 60) + "min"
                : ""
                : travelTimeMin + " min"
                }}</strong
                >
                de trajet</span
                >
              }
              @if (isLoadingRoute) {
                <span class="flex items-center gap-1 text-indigo-500">
                  <svg class="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    ></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Calcul de l'itinéraire…
                </span>
              }
            </div>
          }

          <!-- Adresse validée -->
          @if (validatedAddress && !validationError) {
            <div
              class="mt-2 p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg"
              >
              <div class="flex items-start">
                <svg
                  class="w-4 h-4 text-green-500 mr-1.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
                <div class="flex-1">
                  <p class="text-xs font-medium text-green-900">
                    ✓ {{ validatedAddress.formatted }}
                  </p>
                </div>
              </div>
            </div>
          }

          <!-- Bouton de confirmation - masqué, l'utilisateur confirmera dans location-step -->
          <!--
          <div *ngIf="validatedAddress" class="mt-2">
            <button
              (click)="confirmSelection()"
              class="w-full px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors">
              >
              Confirmer cette adresse
            </button>
          </div>
          -->

          <!-- Spinner de validation -->
          @if (isValidating) {
            <div
              class="mt-3 flex items-center justify-center text-blue-600 dark:text-blue-400"
              >
              <svg class="w-4 h-4 animate-spin mr-1.5" fill="none" viewBox="0 0 24 24">
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span class="text-xs">Validation en cours...</span>
            </div>
          }
        </div>
